import React from "react";
import { gql, useMutation, useSubscription } from "@apollo/client";
import { Product } from "@syos-music/products";

import {
  styled,
  Alert,
  Box,
  Button,
  Divider,
  Stack,
  Typography,
  IconButton,
  Tooltip,
} from "@mui/material";
import { LoadingButton } from "@mui/lab";
import {
  EarHearing as SoundShapingIcon,
  Check as FinalIcon,
  Printer3dNozzle as PrintedIcon,
} from "mdi-material-ui";

import * as CartItem from "components/order/CartItem";

import OrderSidebar from "../index";

import CreateSoundShapingDialog from "../soundShaping/CreateSoundShapingDialog";
import AutoSoundShaping from "../soundShaping/AutoSoundShaping";
import SoundShapingListItem from "../soundShaping/SoundShapingListItem";

const CartItemRoot = styled(Stack)(({ theme, outOfStock }) => ({
  padding: theme.spacing(1),
  borderBottom: `1px solid ${theme.palette.divider}`,

  "&:nth-of-type(odd)": {
    backgroundColor: theme.palette.background.odd,
  },
  "&:nth-of-type(even)": {
    backgroundColor: theme.palette.background.even,
  },
}));

const shouldForwardProp = (prop) => prop !== "many";
const Quantity = styled("span", { shouldForwardProp })(({ theme, many }) => ({
  height: 20,
  minWidth: 20,
  padding: "0 6px",
  borderRadius: 10,
  lineHeight: 1,
  display: "flex",
  alignItems: "center",
  backgroundColor: many && theme.palette.error.main,
  color: many && theme.palette.error.contrastText,
}));

const SUBSCRIPTIONS = {
  saxophone: gql`
    subscription {
      saxophoneMouthpieces {
        id
        files {
          design
          stl {
            id
            name
            url
          }
        }
      }
    }
  `,
  clarinet: gql`
    subscription {
      clarinetMouthpieces {
        id
        files {
          design
          stl {
            id
            name
            url
          }
        }
      }
    }
  `,
};

export default function CartContent({ order }) {
  useSubscription(SUBSCRIPTIONS.saxophone);
  useSubscription(SUBSCRIPTIONS.clarinet);

  const [mutate, { loading }] = useMutation(
    React.useMemo(
      () => gql`
        mutation ($orderId: ID!, $cart: [CartItemInput]) {
          UpdateOrderAdmin(orderId: $orderId, cart: $cart) {
            order {
              id
              ...OrderSidebar_order
            }
          }
        }

        ${OrderSidebar.fragments.order}
      `,
      []
    )
  );

  const updateCart = React.useCallback(
    ({ cart }) => {
      const prepareCartItem = (cartItem) => ({
        id: cartItem.id,
        productId: cartItem.product.id,
        options: cartItem.options,
        quantity: cartItem.quantity,
      });

      return mutate({
        variables: {
          orderId: order.id,
          cart: cart?.map(prepareCartItem),
        },
      });
    },
    [mutate, order.id]
  );

  const handleSubmit = React.useCallback(
    ({ id, quantity, options }) => {
      const cart = order.cart.slice(0);

      const cartItemIndex = cart.findIndex((cartItem) => cartItem.id === id);
      cart[cartItemIndex] = Object.assign({}, cart[cartItemIndex], {
        options,
        quantity,
      });

      return updateCart({ cart })
        .then(() => {})
        .catch(() => true);
    },
    [updateCart, order.cart]
  );

  const handleRemove = React.useCallback(
    (e) => {
      const cart = order.cart.filter(
        ({ id }) => id !== e.currentTarget.dataset.id
      );

      updateCart({ cart });
    },
    [updateCart, order.cart]
  );

  return order.cart.map((cartItem) => (
    <CartItemRoot key={cartItem.id} spacing={0.5}>
      {!order.paymentId && cartItem.stock?.validity === false && (
        <Alert severity="error">Out of stock</Alert>
      )}

      <Stack spacing={1} direction="row" alignItems="center">
        <Box
          sx={{
            flexGrow: 1,
            "& em": {
              color: (t) => t.palette.primary.main,
              fontWeight: 700,
              fontStyle: "normal",
            },
          }}
        >
          <Product.Title
            product={cartItem.product}
            options={cartItem.options}
          />
        </Box>

        <Quantity many={cartItem.quantity > 1} sx={{ flexShrink: 0 }}>
          &times;&nbsp;{cartItem.quantity}
        </Quantity>

        <Box component="strong">
          <Product.Price
            product={cartItem.product}
            options={cartItem.options}
            quantity={cartItem.quantity}
            taxRate={order.taxRate}
            currency={order.currency}
          />
        </Box>
      </Stack>

      <Box sx={{ fontSize: "90%", "& > ul": { m: 0, pl: 2.5 } }}>
        <Product.OptionsList
          product={cartItem.product}
          options={cartItem.options}
        />
      </Box>

      <Stack
        spacing={1}
        direction="row"
        justifyContent="flex-end"
        alignItems="center"
      >
        <CartItem.FormDialog
          onSubmit={handleSubmit}
          isOrderPaid={!!order.paymentId}
          cartItem={cartItem}
        >
          {({ open }) => (
            <Button
              variant="text"
              color="secondary"
              size="small"
              onClick={open}
            >
              Modifier
            </Button>
          )}
        </CartItem.FormDialog>

        {!order.paymentId && (
          <LoadingButton
            variant="text"
            color="secondary"
            size="small"
            loading={loading}
            onClick={handleRemove}
            data-id={cartItem.id}
          >
            Supprimer
          </LoadingButton>
        )}
      </Stack>

      {!!cartItem.product.hasSoundShaping && (
        <>
          <Divider />

          <Stack spacing={1} direction="row" alignItems="center">
            <Tooltip placement="top" title="Bec final ?">
              <FinalIcon />
            </Tooltip>
            <Tooltip placement="top" title="Bec imprimé ?">
              <PrintedIcon />
            </Tooltip>

            <Typography
              variant="h4"
              color="primary"
              noWrap
              sx={{ flexGrow: 1 }}
            >
              Sound shapings
            </Typography>

            <AutoSoundShaping orderId={order.id} cartItem={cartItem} />

            <CreateSoundShapingDialog order={order} cartItem={cartItem}>
              {({ open }) => (
                <Tooltip placement="top" title="Sound shaping manuel">
                  <IconButton size="small" onClick={open}>
                    <SoundShapingIcon />
                  </IconButton>
                </Tooltip>
              )}
            </CreateSoundShapingDialog>
          </Stack>

          <Divider />

          <Box sx={{ "&:empty": { display: "none" } }}>
            {cartItem.soundShapings
              ?.slice(0) // reverse mutate array, copy it
              .reverse()
              .map((soundShaping, idx) => (
                <SoundShapingListItem
                  key={soundShaping.id}
                  order={order}
                  soundShaping={soundShaping}
                  count={cartItem.soundShapings.length - idx}
                  sx={{ m: (t) => t.spacing(0, -1) }}
                />
              ))}
          </Box>
        </>
      )}
    </CartItemRoot>
  ));
}

CartContent.fragments = {
  order: gql`
    fragment OrderSidebar_Cart_CartContent_order on Order {
      id
      paymentId

      currency
      taxRate

      cart {
        id

        product {
          id
          hasSoundShaping
          ...Product_product
        }
        quantity
        options

        soundShapings {
          id
          ...OrderSidebar_SoundShaping_ListItem_soundShaping
        }

        soundShapings {
          id
        }

        stock {
          validity
          quantityLeft
        }

        ...OrderSidebar_SoundShaping_AutoSoundShaping_cartItem
        ...OrderSidebar_SoundShaping_CreateSoundShapingDialog_cartItem
      }

      ...OrderSidebar_SoundShaping_ListItem_order
      ...OrderSidebar_SoundShaping_CreateSoundShapingDialog_order
    }

    ${Product.fragments.product}

    ${SoundShapingListItem.fragments.soundShaping}

    ${AutoSoundShaping.fragments.cartItem}
    ${CreateSoundShapingDialog.fragments.cartItem}

    ${SoundShapingListItem.fragments.order}
    ${CreateSoundShapingDialog.fragments.order}
  `,
};
